import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  NotificacionDto,
  NotificacionFormState,
  NotificacionLogDto,
  SucursalNotificacionCatalogo,
} from "../../../types/ajustes";
import SectionState from "./SectionState";
import {
  getNotificacionLogDescripcion,
  getNotificacionLogEstadoBadge,
} from "./notificacionLogUtils";

interface NotificacionesTabProps {
  registros: NotificacionDto[];
  form: NotificacionFormState;
  enEdicion: boolean;
  cargando: boolean;
  guardando: boolean;
  error: string | null;
  onFormChange: (form: NotificacionFormState) => void;
  onSubmit: () => void;
  onCancelar: () => void;
  onReload: () => void;
  onEditar: (registro: NotificacionDto) => void;
  onEliminar: (registro: NotificacionDto) => void;
  onVerHistorial: (registro: NotificacionDto) => void;
  catalogoSucursales: SucursalNotificacionCatalogo[];
  catalogoCargando: boolean;
  catalogoError: string | null;
  formError: string | null;
  formSuccess: string | null;
  onDismissFeedback: () => void;
  logs: NotificacionLogDto[];
  logsLoading: boolean;
  logsError: string | null;
  onLogsReload: () => void;
  onVerEnvio: (log: NotificacionLogDto) => void;
}

type DestinoOption = {
  value: string;
  empresaCodigo: string;
  empresaNombre: string;
  idsucursal: string;
  sucursalNombre: string;
  label: string;
};

type NotificacionAgrupada = {
  key: string;
  correo: string;
  registros: NotificacionDto[];
  resumenEmpresas: string;
  resumenSucursales: string;
  descripcionResumen: string;
  estadoResumen: string;
  estadoColor: string;
  creadoPrincipal: string;
  creadoSecundario: string | null;
};

const buildDestinoKey = (empresaCodigo: string, idsucursal: string) => {
  const trimmedEmpresa = empresaCodigo.trim();
  const trimmedSucursal = idsucursal.trim();
  if (!trimmedEmpresa) return "";
  return `${trimmedEmpresa}__${trimmedSucursal || "ALL"}`;
};

const formatFecha = (valor?: string | null) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  return fecha.toLocaleDateString();
};

const getEmpresaLabel = (registro: NotificacionDto) => {
  const codigo = registro.empresaCodigo?.trim() ?? "";
  const nombre = registro.empresaNombre?.trim() ?? "";
  if (codigo && nombre) return `${codigo} · ${nombre}`;
  if (codigo) return codigo;
  if (nombre) return nombre;
  return "Sin empresa";
};

const getSucursalLabel = (registro: NotificacionDto) => {
  if (registro.idsucursal === null || registro.idsucursal === undefined) {
    return "Todas las sucursales";
  }
  const codigo = String(registro.idsucursal);
  const nombre = registro.sucursalNombre?.trim() ?? "";
  return nombre ? `${codigo} · ${nombre}` : codigo;
};

const buildResumenLista = (items: string[]) => {
  if (items.length === 0) return "—";
  if (items.length === 1) return items[0];
  return `${items[0]} +${items.length - 1} más`;
};

const NotificacionesTab = ({
  registros,
  form,
  enEdicion,
  cargando,
  guardando,
  error,
  onFormChange,
  onSubmit,
  onCancelar,
  onReload,
  onEditar,
  onEliminar,
  onVerHistorial,
  catalogoSucursales,
  catalogoCargando,
  catalogoError,
  formError,
  formSuccess,
  onDismissFeedback,
  logs,
  logsLoading,
  logsError,
  onLogsReload,
  onVerEnvio,
}: NotificacionesTabProps) => {
  const multiSeleccionActiva = !enEdicion;
  const [detalleActivo, setDetalleActivo] = useState<NotificacionAgrupada | null>(null);
  const [activeListaTab, setActiveListaTab] = useState<"destinatarios" | "historial">("destinatarios");
  const [historialSearch, setHistorialSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const previoEnEdicion = useRef(enEdicion);
  const handleChange = <K extends keyof NotificacionFormState>(
    campo: K,
    valor: NotificacionFormState[K]
  ) => {
    onFormChange({
      ...form,
      [campo]: valor,
    });
  };

  const abrirDetalle = (grupo: NotificacionAgrupada) => setDetalleActivo(grupo);
  const cerrarDetalle = () => setDetalleActivo(null);
  const abrirFormModal = () => {
    if (enEdicion) return;
    setFormModalOpen(true);
  };
  const cerrarFormModal = () => {
    setFormModalOpen(false);
    if (enEdicion) {
      onCancelar();
    }
  };
  useEffect(() => {
    if (enEdicion) {
      setFormModalOpen(true);
    } else if (previoEnEdicion.current && !enEdicion) {
      setFormModalOpen(false);
    }
    previoEnEdicion.current = enEdicion;
  }, [enEdicion]);

  const destinosDisponibles = useMemo(() => {
    const opciones: DestinoOption[] = [];
    const agregados = new Set<string>();
    const agregar = ({
      empresaCodigo,
      empresaNombre,
      idsucursal,
      sucursalNombre,
    }: {
      empresaCodigo: string;
      empresaNombre: string;
      idsucursal: string;
      sucursalNombre: string;
    }) => {
      const key = buildDestinoKey(empresaCodigo, idsucursal);
      if (!key || agregados.has(key)) return;
      agregados.add(key);
      const descripcionSucursal =
        idsucursal.trim().length > 0
          ? `${idsucursal} · ${sucursalNombre || "Sin nombre"}`
          : "Todas las sucursales";
      opciones.push({
        value: key,
        empresaCodigo,
        empresaNombre,
        idsucursal,
        sucursalNombre,
        label: `${empresaCodigo} – ${descripcionSucursal}`,
      });
    };

    catalogoSucursales.forEach((item) => {
      const empresaCodigo = item.empresa?.codigo?.trim();
      if (!empresaCodigo) return;
      agregar({
        empresaCodigo,
        empresaNombre: item.empresa?.nombre ?? "",
        idsucursal:
          item.idsucursal !== null && item.idsucursal !== undefined ? String(item.idsucursal) : "",
        sucursalNombre: item.sucursalNombre ?? "",
      });
    });

    if (form.empresaCodigo.trim()) {
      agregar({
        empresaCodigo: form.empresaCodigo.trim(),
        empresaNombre: form.empresaNombre,
        idsucursal: form.idsucursal,
        sucursalNombre: form.sucursalNombre,
      });
    }

    return opciones;
  }, [
    catalogoSucursales,
    form.empresaCodigo,
    form.empresaNombre,
    form.idsucursal,
    form.sucursalNombre,
  ]);

  const destinoSeleccionado = useMemo(() => {
    if (!form.empresaCodigo.trim()) return "";
    return buildDestinoKey(form.empresaCodigo, form.idsucursal);
  }, [form.empresaCodigo, form.idsucursal]);

  const cambiarDestino = (valor: string) => {
    if (!valor) {
      onFormChange({
        ...form,
        empresaCodigo: "",
        empresaNombre: "",
        idsucursal: "",
        sucursalNombre: "",
        destinosBatch: [],
      });
      return;
    }
    const destino = destinosDisponibles.find((item) => item.value === valor);
    if (!destino) return;
    onFormChange({
      ...form,
      empresaCodigo: destino.empresaCodigo,
      empresaNombre: destino.empresaNombre,
      idsucursal: destino.idsucursal,
      sucursalNombre: destino.sucursalNombre,
      destinosBatch: [
        {
          key: destino.value,
          empresaCodigo: destino.empresaCodigo,
          empresaNombre: destino.empresaNombre,
          idsucursal: destino.idsucursal,
          sucursalNombre: destino.sucursalNombre,
          label: destino.label,
        },
      ],
    });
  };

  const manejarSeleccionDestinos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const select = event.target as unknown as HTMLSelectElement;
    if (!multiSeleccionActiva) {
      cambiarDestino(select.value);
      return;
    }
    const valores = Array.from(select.selectedOptions)
      .map((option) => option.value)
      .filter((value) => value && value !== "null");
    const seleccionados = valores
      .map((valor) => destinosDisponibles.find((destino) => destino.value === valor))
      .filter((destino): destino is DestinoOption => Boolean(destino));
    const lote = seleccionados.map((destino) => ({
      key: destino.value,
      empresaCodigo: destino.empresaCodigo,
      empresaNombre: destino.empresaNombre ?? "",
      idsucursal: destino.idsucursal,
      sucursalNombre: destino.sucursalNombre,
      label: destino.label,
    }));
    onFormChange({
      ...form,
      destinosBatch: lote,
      empresaCodigo: lote[0]?.empresaCodigo ?? "",
      empresaNombre: lote[0]?.empresaNombre ?? "",
      idsucursal: lote[0]?.idsucursal ?? "",
      sucursalNombre: lote[0]?.sucursalNombre ?? "",
    });
  };

  const removerDestinoBatch = (key: string) => {
    if (!multiSeleccionActiva) {
      return;
    }
    const lote = form.destinosBatch.filter((destino) => destino.key !== key);
    onFormChange({
      ...form,
      destinosBatch: lote,
      empresaCodigo: lote[0]?.empresaCodigo ?? "",
      empresaNombre: lote[0]?.empresaNombre ?? "",
      idsucursal: lote[0]?.idsucursal ?? "",
      sucursalNombre: lote[0]?.sucursalNombre ?? "",
    });
  };

  const valoresSeleccionados = multiSeleccionActiva
    ? form.destinosBatch.length > 0
      ? form.destinosBatch.map((destino) => destino.key)
      : destinoSeleccionado
        ? [destinoSeleccionado]
        : []
    : destinoSeleccionado;
  const registrosAgrupados = useMemo<NotificacionAgrupada[]>(() => {
    if (!registros || registros.length === 0) {
      return [];
    }

    const grupos = new Map<
      string,
      {
        correo: string;
        registros: NotificacionDto[];
      }
    >();
    let sinCorreoIndex = 0;

    registros.forEach((registro) => {
      const correoOriginal = registro.correo?.trim() ?? "";
      const tieneCorreo = correoOriginal.length > 0;
      const key = tieneCorreo ? correoOriginal.toLowerCase() : `sin-correo-${sinCorreoIndex++}`;
      const correoMostrar = tieneCorreo ? correoOriginal : "Sin correo";
      if (!grupos.has(key)) {
        grupos.set(key, { correo: correoMostrar, registros: [] });
      }
      grupos.get(key)!.registros.push(registro);
    });

    return Array.from(grupos.entries()).map(([key, { correo, registros: registrosGrupo }]) => {
      const registrosOrdenados = [...registrosGrupo].sort(
        (a, b) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime()
      );
      const empresasUnicas = Array.from(new Set(registrosOrdenados.map(getEmpresaLabel)));
      const sucursalesUnicas = Array.from(new Set(registrosOrdenados.map(getSucursalLabel)));
      const descripcionesUnicas = Array.from(
        new Set(
          registrosOrdenados
            .map((item) => item.descripcion?.trim())
            .filter((descripcion): descripcion is string => Boolean(descripcion))
        )
      );

      const activos = registrosOrdenados.filter((item) => item.activo).length;
      const inactivos = registrosOrdenados.length - activos;
      let estadoResumen = "";
      let estadoColor = "soft-secondary";
      if (activos === registrosOrdenados.length) {
        estadoResumen = "Todos activos";
        estadoColor = "soft-success";
      } else if (activos === 0) {
        estadoResumen = "Todos inactivos";
      } else {
        estadoResumen = `${activos} activos · ${inactivos} inactivos`;
        estadoColor = "soft-warning";
      }

      const descripcionResumen =
        descripcionesUnicas.length === 0
          ? "—"
          : descripcionesUnicas.length === 1
            ? descripcionesUnicas[0]
            : `${descripcionesUnicas[0]} +${descripcionesUnicas.length - 1} más`;

      const masReciente = registrosOrdenados[0]?.creadoEn ?? null;
      const masAntiguo = registrosOrdenados[registrosOrdenados.length - 1]?.creadoEn ?? null;

      return {
        key,
        correo,
        registros: registrosOrdenados,
        resumenEmpresas: buildResumenLista(empresasUnicas),
        resumenSucursales: buildResumenLista(sucursalesUnicas),
        descripcionResumen,
        estadoResumen,
        estadoColor,
        creadoPrincipal: formatFecha(masReciente),
        creadoSecundario:
          masAntiguo && masReciente && masAntiguo !== masReciente ? `Desde ${formatFecha(masAntiguo)}` : null,
      };
    });
  }, [registros]);
  const logsFiltrados = useMemo(() => {
    const termino = historialSearch.trim().toLowerCase();
    if (!termino) return logs;
    return logs.filter((log) => {
      const empresa = `${log.empresaCodigo ?? ""} ${log.empresaNombre ?? ""}`.toLowerCase();
      const sucursal = `${log.idsucursal ?? ""} ${log.sucursalNombre ?? ""}`.toLowerCase();
      const destino = (log.correo ?? "").toLowerCase();
      const estado = log.estado.toLowerCase();
      const detalle = (log.mensaje ?? "").toLowerCase();
      return (
        empresa.includes(termino) ||
        sucursal.includes(termino) ||
        destino.includes(termino) ||
        estado.includes(termino) ||
        detalle.includes(termino)
      );
    });
  }, [logs, historialSearch]);

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between flex-column flex-md-row gap-3 mb-4">
          <div>
            <CardTitle tag="h5" className="mb-1">
              Canales para avisos automáticos
            </CardTitle>
            <p className="text-muted mb-0">
              Define quiénes reciben los correos de confirmación de rendición.
            </p>
            {catalogoCargando && (
              <small className="text-muted d-block mt-1">Cargando empresas y sucursales...</small>
            )}
            {catalogoError && (
              <small className="text-danger d-block mt-1">{catalogoError}</small>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end">
            <Button color="primary" size="sm" onClick={abrirFormModal} disabled={enEdicion || guardando}>
              {enEdicion ? "Editando registro…" : "Agregar destinatario"}
            </Button>
            <Button color="light" size="sm" onClick={onReload} disabled={cargando}>
              Actualizar
            </Button>
          </div>
        </div>

        <Modal isOpen={formModalOpen} toggle={cerrarFormModal} size="lg">
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <ModalHeader toggle={cerrarFormModal}>
              {enEdicion ? "Editar destinatario" : "Agregar destinatario"}
            </ModalHeader>
            <ModalBody>
              <fieldset disabled={guardando}>
                <Row className="gy-3">
                  <Col md={12} lg={6}>
                    <FormGroup>
                      <Label for="notif-destino">Empresa y sucursal</Label>
                      <Input
                        id="notif-destino"
                        type="select"
                        multiple={multiSeleccionActiva}
                        value={valoresSeleccionados}
                        onChange={manejarSeleccionDestinos}
                        disabled={catalogoCargando}
                        size={multiSeleccionActiva ? Math.min(destinosDisponibles.length + 1, 8) : undefined}
                      >
                        <option value="">Seleccioná destino</option>
                        {destinosDisponibles.map((destino) => (
                          <option key={destino.value} value={destino.value}>
                            {destino.label}
                          </option>
                        ))}
                      </Input>
                      {multiSeleccionActiva && (
                        <small className="text-muted d-block mt-1">
                          Mantené presionadas las teclas Ctrl o Shift para elegir varias sucursales.
                        </small>
                      )}
                      {multiSeleccionActiva && form.destinosBatch.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {form.destinosBatch.map((destino) => (
                            <Badge
                              key={destino.key}
                              color="soft-primary"
                              className="d-flex align-items-center gap-2"
                            >
                              <span>{destino.label}</span>
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none text-primary"
                                onClick={() => removerDestinoBatch(destino.key)}
                                aria-label={`Quitar ${destino.label}`}
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="notif-correo">Correo destino</Label>
                      <Input
                        id="notif-correo"
                        type="email"
                        placeholder="tesoreria@empresa.com"
                        value={form.correo}
                        onChange={(e) => handleChange("correo", e.target.value)}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6} lg={3}>
                    <FormGroup>
                      <Label for="notif-descripcion">Descripción</Label>
                      <Input
                        id="notif-descripcion"
                        type="textarea"
                        rows={1}
                        placeholder="Ej. Supervisores y auditoría"
                        value={form.descripcion}
                        onChange={(e) => handleChange("descripcion", e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <FormGroup>
                      <Label className="d-block mb-2">Estado</Label>
                      <div className="form-check form-switch">
                        <Input
                          id="notif-activo"
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={form.activo}
                          onChange={(e) => handleChange("activo", e.target.checked)}
                        />
                        <Label className="form-check-label" htmlFor="notif-activo">
                          {form.activo ? "Activo" : "Inactivo"}
                        </Label>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <div className="mt-3">
                  {formError && (
                    <Alert color="danger" toggle={onDismissFeedback}>
                      {formError}
                    </Alert>
                  )}
                  {formSuccess && (
                    <Alert color="success" toggle={onDismissFeedback}>
                      {formSuccess}
                    </Alert>
                  )}
                </div>
              </fieldset>
            </ModalBody>
            <ModalFooter className="d-flex flex-wrap gap-2">
              <Button color="primary" type="submit" disabled={guardando}>
                {guardando ? "Guardando…" : enEdicion ? "Guardar cambios" : "Agregar destinatario"}
              </Button>
              <Button color="light" type="button" onClick={cerrarFormModal} disabled={guardando}>
                {enEdicion ? "Cancelar edición" : "Cerrar"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <div className="mt-4">
          <Nav tabs className="nav-tabs-custom">
            <NavItem>
              <NavLink
                role="button"
                className={activeListaTab === "destinatarios" ? "" : undefined}
                active={activeListaTab === "destinatarios"}
                onClick={() => setActiveListaTab("destinatarios")}
              >
                Destinatarios configurados
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                role="button"
                className={activeListaTab === "historial" ? "" : undefined}
                active={activeListaTab === "historial"}
                onClick={() => setActiveListaTab("historial")}
              >
                Historial reciente de envíos
              </NavLink>
            </NavItem>
          </Nav>
          <div className="pt-3">
            {activeListaTab === "destinatarios" ? (
              <>
                <SectionState
                  loading={cargando}
                  error={error}
                  description="Cargando destinatarios..."
                  onRetry={onReload}
                />
                {!cargando && !error && (
                  <div className="table-responsive mt-3">
                    <Table className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Empresa</th>
                          <th>Sucursal</th>
                          <th>Correo</th>
                          <th>Descripción</th>
                          <th>Estado</th>
                          <th>Creado</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrosAgrupados.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">
                              No hay destinatarios configurados.
                            </td>
                          </tr>
                        ) : (
                          registrosAgrupados.map((grupo) => (
                            <tr key={grupo.key}>
                              <td>
                                <div className="fw-semibold text-body">{grupo.resumenEmpresas}</div>
                                <small className="text-muted">Empresas relacionadas</small>
                              </td>
                              <td>
                                <div className="text-body">{grupo.resumenSucursales}</div>
                                <small className="text-muted">
                                  {grupo.registros.length === 1
                                    ? "1 sucursal asignada"
                                    : `${grupo.registros.length} sucursales asignadas`}
                                </small>
                              </td>
                              <td>{grupo.correo}</td>
                              <td>{grupo.descripcionResumen}</td>
                              <td>
                                <Badge color={grupo.estadoColor}>{grupo.estadoResumen}</Badge>
                              </td>
                              <td>
                                <div>{grupo.creadoPrincipal}</div>
                                {grupo.creadoSecundario && (
                                  <small className="text-muted">{grupo.creadoSecundario}</small>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  color="soft-secondary"
                                  size="sm"
                                  className="text-nowrap"
                                  onClick={() => abrirDetalle(grupo)}
                                >
                                  Ver sucursales
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mb-3">
                  <p className="text-muted mb-0">
                    Visualizá los últimos eventos registrados al confirmar rendiciones, incluso cuando
                    no se encontraron destinatarios.
                  </p>
                </div>
                <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 mb-3">
                  <Input
                    type="search"
                    placeholder="Buscar por empresa, sucursal, correo o estado"
                    value={historialSearch}
                    onChange={(e) => setHistorialSearch(e.target.value)}
                    disabled={logsLoading}
                    className="flex-grow-1"
                  />
                  <Button color="light" size="sm" onClick={onLogsReload} disabled={logsLoading}>
                    Actualizar
                  </Button>
                </div>
                <SectionState
                  loading={logsLoading}
                  error={logsError}
                  description="Cargando historial..."
                  onRetry={onLogsReload}
                />
                {!logsLoading && !logsError && (
                  <>
                    {logsFiltrados.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        {historialSearch.trim()
                          ? "No hay coincidencias para tu búsqueda."
                          : "Aún no hay movimientos registrados."}
                      </div>
                    ) : (
                      <div className="table-responsive mt-3">
                        <Table className="align-middle table-sm">
                          <thead className="table-light">
                            <tr>
                              <th>Fecha</th>
                              <th>Empresa</th>
                              <th>Sucursal</th>
                              <th>Destino</th>
                              <th>Estado</th>
                              <th>Detalle</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logsFiltrados.map((log) => {
                              const badge = getNotificacionLogEstadoBadge(log.estado);
                              return (
                                <tr key={log.id}>
                                  <td className="text-nowrap">{new Date(log.intentoEn).toLocaleString()}</td>
                                  <td>
                                    <div className="fw-semibold">{log.empresaCodigo ?? "Sin código"}</div>
                                    <small className="text-muted">{log.empresaNombre ?? "Sin nombre"}</small>
                                  </td>
                                  <td>
                                    <div>{log.idsucursal ?? "Todas"}</div>
                                    <small className="text-muted">
                                      {log.sucursalNombre ?? "Sin nombre"}
                                    </small>
                                  </td>
                                  <td>{log.correo}</td>
                                  <td>
                                    <span className={badge.className}>{badge.label}</span>
                                  </td>
                                  <td className="text-muted">{getNotificacionLogDescripcion(log)}</td>
                                  <td className="text-nowrap">
                                    {log.cuerpoHtml ? (
                                      <Button color="link" size="sm" onClick={() => onVerEnvio(log)}>
                                        Ver envío
                                      </Button>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <Modal isOpen={Boolean(detalleActivo)} toggle={cerrarDetalle} size="lg">
          <ModalHeader toggle={cerrarDetalle}>
            {detalleActivo ? `Sucursales para ${detalleActivo.correo}` : "Sucursales"}
          </ModalHeader>
          <ModalBody>
            {detalleActivo && (
              <>
                <div className="mb-3">
                  <div className="fw-semibold">{detalleActivo.resumenEmpresas}</div>
                  <small className="text-muted">
                    {detalleActivo.registros.length === 1
                      ? "1 registro asociado"
                      : `${detalleActivo.registros.length} registros asociados`}
                  </small>
                </div>
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Empresa</th>
                        <th>Sucursal</th>
                        <th>Descripción</th>
                        <th>Estado</th>
                        <th>Creado</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleActivo.registros.map((registro) => (
                        <tr key={registro.id}>
                          <td>
                            <div className="fw-semibold text-body">
                              {registro.empresaCodigo ?? "Sin código"}
                            </div>
                            <small className="text-muted">{registro.empresaNombre ?? "Sin nombre"}</small>
                          </td>
                          <td>
                            <div className="text-body">{registro.idsucursal ?? "Todas"}</div>
                            <small className="text-muted">{registro.sucursalNombre ?? "Sin nombre"}</small>
                          </td>
                          <td>{registro.descripcion ?? "—"}</td>
                          <td>
                            <Badge color={registro.activo ? "soft-success" : "soft-secondary"}>
                              {registro.activo ? "Activo" : "Inactivo"}
                            </Badge>
                          </td>
                          <td>{formatFecha(registro.creadoEn)}</td>
                          <td className="text-center">
                            <UncontrolledDropdown>
                              <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
                                <i className="ri-more-fill align-middle" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem onClick={() => onVerHistorial(registro)}>
                                  <i className="ri-time-line align-bottom me-2 text-muted" />
                                  Historial
                                </DropdownItem>
                                <DropdownItem onClick={() => onEditar(registro)}>
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                                  Editar
                                </DropdownItem>
                                <DropdownItem onClick={() => onEliminar(registro)}>
                                  <i className="ri-delete-bin-line align-bottom me-2 text-muted" />
                                  Eliminar
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={cerrarDetalle}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default NotificacionesTab;
